import { Icon } from "@iconify/react";
import { Dialog, DialogProps } from "@mui/material";
import { useState } from "react";
import { toast } from "react-toastify";
import { useInternalTransfer } from "../../queries";
import { Employee } from "../../types/Employee";
import { Box, ButtonPrimary, ButtonSecondary, HStack, VStack } from "../utils";

export type InternalTransferDialogProps = {
  employees: Employee[];
  onClose: () => void;
} & DialogProps;

function InternalTransferDialog({
  employees,
  onClose,
  ...dialogProps
}: InternalTransferDialogProps) {
  const { mutate: transfer, isLoading } = useInternalTransfer();
  const [oldEmployeeId, setOldEmployeeId] = useState<string>(
    employees[0]?.id || ""
  );

  const newEmployeeId =
    employees.find((emp) => emp.id !== oldEmployeeId)?.id || "";
  const oldEmployee = employees.find((emp) => emp.id === oldEmployeeId);
  const newEmployee = employees.find((emp) => emp.id === newEmployeeId);

  const handleSwap = () => {
    setOldEmployeeId(newEmployeeId);
  };

  const handleTransfer = () => {
    transfer(
      { oldEmployeeId, newEmployeeId },
      {
        onSuccess: () => {
          toast("Internal transfer request raised successfully", {
            type: "success",
            autoClose: 2000,
          });
          onClose();
        },
        onError: (err: any) => {
          toast(
            err?.response?.data?.errorRaw ||
              err?.response?.data?.reason ||
              "Something went wrong",
            {
              type: "error",
              autoClose: 5000,
            }
          );
        },
      }
    );
  };

  return (
    <Dialog {...dialogProps} maxWidth="sm" fullWidth>
      <VStack className="p-8 bg-white rounded gap-6">
        <HStack className="justify-between items-center">
          <p className="text-lg font-medium text-headerColor">
            Internal Transfer
          </p>
          <Icon
            icon="material-symbols:close-rounded"
            height={24}
            className="cursor-pointer text-gray-400 hover:text-gray-600"
            onClick={onClose}
          />
        </HStack>

        <VStack className="gap-2 p-3 bg-red-50 rounded border border-red-200">
          <p className="text-xs text-gray-500 font-medium">
            Transfer From (will be resigned)
          </p>
          <HStack className="items-center gap-2">
            <Icon
              icon="mdi:account-arrow-right"
              height={20}
              className="text-red-500"
            />
            <VStack>
              <p className="text-sm font-semibold text-gray-dark">
                {oldEmployee?.employeeName || "-"}
              </p>
              <p className="text-xs text-gray-500">
                {oldEmployee?.employeeIdentificationString || "-"} •{" "}
                {oldEmployee?.employeeDesignation || "-"}
              </p>
            </VStack>
          </HStack>
        </VStack>

        <HStack className="justify-center">
          <button
            onClick={handleSwap}
            className="p-2 rounded-full border border-gray-300 hover:bg-gray-100 transition-colors"
            title="Swap employees"
          >
            <Icon
              icon="mdi:swap-vertical"
              height={20}
              className="text-gray-500"
            />
          </button>
        </HStack>

        <VStack className="gap-2 p-3 bg-green-50 rounded border border-green-200">
          <p className="text-xs text-gray-500 font-medium">
            Transfer To (grants will be transferred)
          </p>
          <HStack className="items-center gap-2">
            <Icon
              icon="mdi:account-arrow-left"
              height={20}
              className="text-green-600"
            />
            <VStack>
              <p className="text-sm font-semibold text-gray-dark">
                {newEmployee?.employeeName || "-"}
              </p>
              <p className="text-xs text-gray-500">
                {newEmployee?.employeeIdentificationString || "-"} •{" "}
                {newEmployee?.employeeDesignation || "-"}
              </p>
            </VStack>
          </HStack>
        </VStack>

        <HStack className="gap-4 p-2 text-blue-800 bg-blue-100 rounded">
          <VStack>
            <Icon
              icon="material-symbols:info-outline-rounded"
              height={24}
              width={24}
              className="text-blue-800"
            />
          </VStack>
          <VStack>
            <p className="text-xs">
              All grants from <strong>{oldEmployee?.employeeName}</strong> will
              be transferred to <strong>{newEmployee?.employeeName}</strong>.{" "}
              {oldEmployee?.employeeName} will be marked as resigned.
            </p>
          </VStack>
        </HStack>

        <HStack className="justify-between pt-2 gap-4">
          <ButtonSecondary
            onClick={onClose}
            className="text-gray-400 bg-slate-50"
          >
            Cancel
          </ButtonSecondary>
          <ButtonPrimary onClick={handleTransfer} loading={isLoading}>
            Confirm Transfer
          </ButtonPrimary>
        </HStack>
      </VStack>
    </Dialog>
  );
}

export default InternalTransferDialog;
